import {
  CSSProperties, ReactElement, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon,
} from '../icons';
import {
  CalendarDay, CalendarFooter, CalendarFooterButton, CalendarGrid, CalendarHeader, CalendarNavButton,
  CalendarOutsideDay, CalendarTitle, CalendarWeekDay, DATE_SHEET_BREAKPOINT, DatePopover, DateTrigger,
  DateTriggerValue,
} from './styles';

const POPOVER_GAP = 8;
const VIEWPORT_MARGIN = 8;

const MONTH_KEYS = ['janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
/** A semana começa no domingo, como no design do ajuste. */
const WEEK_DAY_KEYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'];

interface ViewMonth {
  year: number;
  month: number;
}

interface PopoverPosition {
  top: number;
  left: number;
  width: number;
}

type CalendarCell = { kind: 'outside'; day: number; key: string } | { kind: 'day'; day: number; iso: string };

function toIso(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseIso(value: string): ViewMonth | null {
  const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(value);
  return match ? { year: Number(match[1]), month: Number(match[2]) - 1 } : null;
}

function formatIso(value: string): string {
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
}

function todayIso(): string {
  const now = new Date();
  return toIso(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Mês do valor, ou o mês atual quando o campo está vazio. */
function initialView(value: string): ViewMonth {
  const now = new Date();
  return parseIso(value) ?? { year: now.getFullYear(), month: now.getMonth() };
}

function shiftMonth({ year, month }: ViewMonth, delta: number): ViewMonth {
  const date = new Date(year, month + delta, 1);
  return { year: date.getFullYear(), month: date.getMonth() };
}

/** Semanas completas do mês: os dias dos meses vizinhos preenchem a primeira e a última semana. */
function buildMonthCells({ year, month }: ViewMonth): CalendarCell[] {
  const leading = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  const trailing = (7 - ((leading + daysInMonth) % 7)) % 7;
  return [
    ...Array.from({ length: leading }, (_, index): CalendarCell => {
      const day = daysInPrevMonth - leading + index + 1;
      return { kind: 'outside', day, key: `prev-${day}` };
    }),
    ...Array.from({ length: daysInMonth }, (_, index): CalendarCell => ({
      kind: 'day', day: index + 1, iso: toIso(year, month, index + 1),
    })),
    ...Array.from({ length: trailing }, (_, index): CalendarCell => ({ kind: 'outside', day: index + 1, key: `next-${index + 1}` })),
  ];
}

/** Ancorado embaixo do campo, na mesma largura; acima quando não cabe. No celular, folha na base (CSS). */
function computePosition(trigger: HTMLElement, popover: HTMLElement): PopoverPosition | 'sheet' {
  if (window.innerWidth <= DATE_SHEET_BREAKPOINT) return 'sheet';
  const rect = trigger.getBoundingClientRect();
  const height = popover.offsetHeight;
  const fitsBelow = rect.bottom + POPOVER_GAP + height + VIEWPORT_MARGIN <= window.innerHeight;
  const top = fitsBelow || rect.top - POPOVER_GAP - height < VIEWPORT_MARGIN
    ? rect.bottom + POPOVER_GAP
    : rect.top - POPOVER_GAP - height;
  return { top, left: rect.left, width: rect.width };
}

function popoverStyle(position: PopoverPosition | 'sheet' | null): CSSProperties {
  if (position === 'sheet') return {};
  // Antes de medir, renderiza invisível para calcular a altura sem piscar na posição errada.
  return position ?? { visibility: 'hidden', top: 0, left: 0 };
}

interface DateFieldProps {
  id: string;
  /** AAAA-MM-DD, ou vazio. */
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** Dias anteriores (AAAA-MM-DD) ficam desabilitados, exceto o valor atual. */
  min?: string;
  invalid?: boolean;
  /** Ref do react-hook-form, para focar o campo quando a validação falha. */
  inputRef?: (element: HTMLButtonElement | null) => void;
  testId?: string;
}

/**
 * Campo de data com calendário embaixo, na largura do campo: clicar no dia já seleciona e fecha;
 * no rodapé, "Limpar" esvazia o campo e "Hoje" seleciona a data de hoje.
 */
export function DateField({
  id, value, onChange, onBlur, min, invalid = false, inputRef, testId,
}: Readonly<DateFieldProps>): ReactElement {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<ViewMonth>(() => initialView(value));
  const [position, setPosition] = useState<PopoverPosition | 'sheet' | null>(null);

  function setTriggerRef(element: HTMLButtonElement | null): void {
    triggerRef.current = element;
    inputRef?.(element);
  }

  function openPopover(): void {
    setView(initialView(value));
    setPosition(null);
    setOpen(true);
  }

  /** `returnFocus` só quando o fechamento vem do próprio calendário (seleção, rodapé ou Escape). */
  function closePopover(returnFocus = true): void {
    setOpen(false);
    onBlur?.();
    if (returnFocus) triggerRef.current?.focus();
  }

  function select(iso: string): void {
    if (iso !== value) onChange(iso);
    closePopover();
  }

  // Reposiciona também ao trocar de mês: a altura muda (5 ou 6 semanas) e importa quando o calendário abre acima.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !popoverRef.current) return undefined;
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    function reposition(): void {
      setPosition(computePosition(trigger, popover));
    }
    reposition();
    // Captura para acompanhar também a rolagem do corpo do modal.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, view.year, view.month]);

  useEffect(() => {
    if (!open) return undefined;
    function onMouseDown(event: MouseEvent): void {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      closePopover(false);
    }
    // Na captura do document, o Escape fecha só o calendário, sem chegar ao listener que fecha o modal.
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      closePopover();
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  });

  const today = todayIso();
  const isDisabled = (iso: string): boolean => !!min && iso < min && iso !== value;

  return (
    <>
      <DateTrigger
        id={id}
        ref={setTriggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={invalid}
        $open={open}
        $invalid={invalid}
        onClick={() => (open ? closePopover() : openPopover())}
        data-testid={testId}
      >
        <CalendarIcon size={18} />
        <DateTriggerValue $placeholder={!value}>{value ? formatIso(value) : t('selecioneUmaData')}</DateTriggerValue>
        <ChevronDownIcon size={18} />
      </DateTrigger>

      {open ? createPortal(
        <DatePopover ref={popoverRef} role="dialog" aria-label={t('selecioneUmaData')} style={popoverStyle(position)}>
          <CalendarHeader>
            <CalendarNavButton type="button" aria-label={t('ecoSimplificadoMesAnterior')} onClick={() => setView(shiftMonth(view, -1))}>
              <ChevronLeftIcon size={16} />
            </CalendarNavButton>
            <CalendarTitle aria-live="polite">{`${t(`mesesDoAno.${MONTH_KEYS[view.month]}`)} ${view.year}`}</CalendarTitle>
            <CalendarNavButton type="button" aria-label={t('ecoSimplificadoProximoMes')} onClick={() => setView(shiftMonth(view, 1))}>
              <ChevronRightIcon size={16} />
            </CalendarNavButton>
          </CalendarHeader>

          <CalendarGrid>
            {WEEK_DAY_KEYS.map((key) => (
              <CalendarWeekDay key={key} title={t(`diasDaSemana.${key}`)}>{t(`diasDaSemana.${key}`).charAt(0)}</CalendarWeekDay>
            ))}
            {buildMonthCells(view).map((cell) => {
              if (cell.kind === 'outside') return <CalendarOutsideDay key={cell.key} aria-hidden>{cell.day}</CalendarOutsideDay>;
              return (
                <CalendarDay
                  key={cell.iso}
                  type="button"
                  $active={cell.iso === value}
                  $today={cell.iso === today}
                  aria-pressed={cell.iso === value}
                  aria-label={formatIso(cell.iso)}
                  disabled={isDisabled(cell.iso)}
                  onClick={() => select(cell.iso)}
                  data-testid={testId ? `${testId}-dia-${cell.iso}` : undefined}
                >
                  {cell.day}
                </CalendarDay>
              );
            })}
          </CalendarGrid>

          <CalendarFooter>
            <CalendarFooterButton type="button" disabled={!value} onClick={() => select('')}>{t('limpar')}</CalendarFooterButton>
            <CalendarFooterButton type="button" $primary disabled={isDisabled(today)} onClick={() => select(today)}>{t('hoje')}</CalendarFooterButton>
          </CalendarFooter>
        </DatePopover>,
        document.body,
      ) : null}
    </>
  );
}
